import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  Menu,
  MenuItem
} from '@mui/material';
import {
  AdminPanelSettings,
  Refresh,
  Edit,
  AccountBalanceWallet,
  Search,
  MoreVert,
  Person,
  Email,
  TrendingUp,
  TrendingDown,
  Logout
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import API from '../api/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalBalance: 0,
    adminCount: 0
  });

  useEffect(() => {
    // Check if user is admin, if not redirect to dashboard
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    
    fetchUsers();
  }, [user, navigate]);

  useEffect(() => {
    // Filter users based on search term
    if (searchTerm === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/admin/users');
      setUsers(data.users);
      setFilteredUsers(data.users);
      
      // Calculate stats
      const totalBalance = data.users.reduce((sum, user) => sum + (user.accountBalance || 0), 0);
      const adminCount = data.users.filter(u => u.role === 'admin').length;
      
      setStats({
        totalUsers: data.users.length,
        totalBalance: totalBalance,
        adminCount: adminCount
      });
    } catch (error) {
      console.error('Failed to fetch users:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to fetch users',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBalance = async () => {
    if (!selectedUser) return;
    
    const balance = parseFloat(newBalance);
    if (isNaN(balance) || balance < 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid balance amount',
        severity: 'error'
      });
      return;
    }
    
    setUpdating(true);
    try {
      await API.put(`/admin/users/${selectedUser._id}/balance`, { balance });
      
      setSnackbar({
        open: true,
        message: `Balance updated for ${selectedUser.name}`,
        severity: 'success'
      });
      
      // Refresh user list
      await fetchUsers();
      setBalanceDialogOpen(false);
      setNewBalance('');
      setSelectedUser(null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update balance',
        severity: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  const openBalanceDialog = (user) => {
    setSelectedUser(user);
    setNewBalance(user.accountBalance?.toString() || '0');
    setBalanceDialogOpen(true);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  // Show loading while checking auth
  if (!user) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#0A0A0A'
      }}>
        <CircularProgress sx={{ color: '#00FF87' }} />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#0A0A0A'
      }}>
        <CircularProgress sx={{ color: '#00FF87' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#0A0A0A'
    }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 0,
          bgcolor: '#0A0A0A',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          px: 3,
          py: 2,
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AdminPanelSettings sx={{ color: '#00FF87' }} />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                color: '#fff'
              }}
            >
              Admin Dashboard
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip 
              avatar={<Avatar sx={{ bgcolor: '#00FF87', color: '#000' }}>A</Avatar>}
              label={user?.name || 'Admin'}
              onClick={handleMenuOpen}
              sx={{ 
                bgcolor: '#111',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: '#1A1A1A' }
              }}
            />
            <IconButton onClick={fetchUsers} sx={{ color: '#00FF87' }}>
              <Refresh />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: '#111',
            border: '1px solid rgba(255,255,255,0.05)',
            borderRadius: 2,
            minWidth: 150
          }
        }}
      >
        <MenuItem onClick={() => {
          handleMenuClose();
          navigate('/dashboard');
        }} sx={{ color: '#fff' }}>
          Go to Dashboard
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: '#FF4444' }}>
          <Logout sx={{ fontSize: 16, mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>

      <Container maxWidth="xl" sx={{ px: 3, py: 3 }}>
        {/* Stats Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 3, mb: 4 }}>
          <Card sx={{ 
            borderRadius: 3,
            bgcolor: '#111',
            border: '1px solid rgba(255,255,255,0.05)'
          }}>
            <CardContent>
              <Typography variant="caption" sx={{ color: '#666' }}>Total Users</Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#fff', mt: 1 }}>
                {stats.totalUsers}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Person sx={{ fontSize: 14, color: '#00FF87' }} />
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {stats.adminCount} Administrators
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ 
            borderRadius: 3,
            bgcolor: '#111',
            border: '1px solid rgba(255,255,255,0.05)'
          }}>
            <CardContent>
              <Typography variant="caption" sx={{ color: '#666' }}>Total Assets Under Management</Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#fff', mt: 1 }}>
                {formatCurrency(stats.totalBalance)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <TrendingUp sx={{ fontSize: 14, color: '#00FF87' }} />
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Across all user accounts
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ 
            borderRadius: 3,
            bgcolor: '#111',
            border: '1px solid rgba(255,255,255,0.05)'
          }}>
            <CardContent>
              <Typography variant="caption" sx={{ color: '#666' }}>Average Balance</Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#fff', mt: 1 }}>
                {formatCurrency(stats.totalUsers > 0 ? stats.totalBalance / stats.totalUsers : 0)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <AccountBalanceWallet sx={{ fontSize: 14, color: '#60EFFF' }} />
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Per user
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Users Table */}
        <Card sx={{ 
          borderRadius: 3,
          bgcolor: '#111',
          border: '1px solid rgba(255,255,255,0.05)'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
                Registered Users
              </Typography>
              
              <TextField
                placeholder="Search by name or email..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#666', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  sx: {
                    color: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.1)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.2)'
                    }
                  }
                }}
                sx={{ minWidth: 250 }}
              />
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <TableCell sx={{ color: '#666', fontWeight: 600 }}>User</TableCell>
                    <TableCell sx={{ color: '#666', fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ color: '#666', fontWeight: 600 }}>Role</TableCell>
                    <TableCell sx={{ color: '#666', fontWeight: 600, textAlign: 'right' }}>Balance</TableCell>
                    <TableCell sx={{ color: '#666', fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user._id} sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#00FF87', color: '#000', width: 32, height: 32 }}>
                            {getInitials(user.name)}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#fff' }}>
                            {user.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email sx={{ fontSize: 14, color: '#666' }} />
                          <Typography variant="body2" sx={{ color: '#fff' }}>
                            {user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={user.role === 'admin' ? 'Admin' : 'User'}
                          size="small"
                          sx={{ 
                            bgcolor: user.role === 'admin' ? 'rgba(0,255,135,0.1)' : 'rgba(96,239,255,0.1)',
                            color: user.role === 'admin' ? '#00FF87' : '#60EFFF',
                            fontSize: '0.7rem'
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                          {formatCurrency(user.accountBalance || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <IconButton 
                          onClick={() => openBalanceDialog(user)}
                          sx={{ 
                            color: '#00FF87',
                            '&:hover': { bgcolor: 'rgba(0,255,135,0.1)' }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredUsers.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  No users found
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Update Balance Dialog */}
      <Dialog 
        open={balanceDialogOpen} 
        onClose={() => !updating && setBalanceDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#111',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.05)',
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          Update Account Balance
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedUser && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ bgcolor: '#00FF87', color: '#000' }}>
                  {getInitials(selectedUser.name)}
                </Avatar>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#fff' }}>
                    {selectedUser.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {selectedUser.email}
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                Current Balance
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#00FF87', mb: 2 }}>
                {formatCurrency(selectedUser.accountBalance || 0)}
              </Typography>
              
              <TextField
                fullWidth
                label="New Balance"
                type="number"
                value={newBalance}
                onChange={(e) => setNewBalance(e.target.value)}
                placeholder="Enter new balance amount"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: '#666' },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  }
                }}
              />
              
              <Alert severity="info" sx={{ bgcolor: 'rgba(96,239,255,0.1)', color: '#60EFFF', mt: 2 }}>
                This will create a transaction record and notify the user
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            onClick={() => setBalanceDialogOpen(false)} 
            disabled={updating}
            sx={{ color: '#666', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateBalance} 
            disabled={updating}
            variant="contained"
            sx={{
              bgcolor: '#00FF87',
              color: '#000',
              textTransform: 'none',
              '&:hover': { bgcolor: '#00E077' },
              '&.Mui-disabled': { bgcolor: 'rgba(0,255,135,0.3)' }
            }}
          >
            {updating ? <CircularProgress size={24} sx={{ color: '#000' }} /> : 'Update Balance'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ bgcolor: snackbar.severity === 'success' ? '#00FF87' : '#FF4444', color: '#000' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;